// @ts-nocheck
/**
 * One-tap recipe star ratings (styles: assets/vhp-rating.css; API: POST /api/rating on vhp-planner).
 * A tap is the rating: no form, no login. The first tap makes a random key for this recipe on this device, so a
 * later tap changes the rating instead of adding one, and ratings can't be linked across recipes. Every star counts
 * the same (no review gating, no incentive: UWG / Google review rules).
 *
 *   window.vhpRating.widget({ id, source, onDone(result) })  // element: 5 star buttons + status line
 *   window.vhpRating.mine(id)     // stars this device gave, or 0
 *   window.vhpRating.canAsk(id)   // prompt allowed: not rated here, not "Überspringen" in the last 14 days
 *   window.vhpRating.skip()       // quiet period for prompts (the page block is never a prompt)
 *
 *   window.vhpRating.block({ id, source, review, photo, note, … })  // the whole rating block, for JS-built places
 *   window.vhpRating.mount(root)  // mounts [data-vhp-rate] / [data-vrev] in root (the recipe sheet, per body)
 *
 * The block: [data-vhp-rate] (snippets/vhp-reviews.liquid part 'rate', in every recipe body) gets a widget; after a tap, every
 * [data-vhp-rating-summary="<id>"] shows the new average. Fires "vhp:rated" { id, stars, value, count } on document.
 *
 * Written review ([data-vhp-rate][data-review]): after the tap (or "Bewertung schreiben" once rated) an optional form
 * asks for a few words, first name and a photo; POST /api/review with the same key, so it updates this device's
 * rating instead of adding one. Recipe reviews are never used for marketing, so no consent there; a host with
 * [data-review-consent] (planner reviews, logged-in subscribers only) adds the marketing checkbox. Text and photo
 * show after approval (snippets/vhp-reviews.liquid).
 * window.vhpRating.reviewForm(host, { target: 'planner', onLater, onSent }) is the same form for planner reviews, with
 * its own stars (assets/vhp-planner-review.js).
 * The list's "Alle anzeigen" and photo viewer ([data-vrev]) are wired here too.
 */
(() => {
  'use strict';

  const tr = window.vhpT; // snippets/vhp-i18n.liquid

  if (window.vhpRating) return;

  const STORE = 'vhp_rating';
  const QUIET_MS = 14 * 24 * 60 * 60 * 1000;
  const DEFAULT_API = 'https://vhp-planner.vercel.app';
  const STAR =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5L2.5 9.4l6.6-.9z"/></svg>';

  // { r: { id: stars }, k: { id: key }, w: { id: review sent at }, quiet: until (ms) }. Written only when someone taps
  // a star, "Überspringen" or sends a review.
  const load = () => {
    try {
      const data = JSON.parse(localStorage.getItem(STORE) || '{}');
      return { r: data.r || {}, k: data.k || {}, w: data.w || {}, quiet: Number(data.quiet) || 0 };
    } catch (_) {
      return { r: {}, k: {}, w: {}, quiet: 0 };
    }
  };
  const save = (data) => {
    try {
      localStorage.setItem(STORE, JSON.stringify(data));
    } catch (_) {}
  };

  const keyFor = (data, id) => {
    if (data.k[id]) return data.k[id];
    const bytes = new Uint8Array(18);
    crypto.getRandomValues(bytes);
    const key = btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    data.k[id] = key;
    return key;
  };

  // layout/theme.liquid: { apiBase, customerId, token, email, … }
  const account = () => {
    try {
      return JSON.parse(document.getElementById('vhp-account-config')?.textContent || '{}') || {};
    } catch (_) {
      return {};
    }
  };
  const apiBase = () => account().apiBase || DEFAULT_API;

  const lang = () => document.documentElement.lang || 'de';
  const fmtValue = (value) => Number(value).toLocaleString(lang(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const summary = ({ value, count }) => tr('rating.summary', { value: fmtValue(value), n: count });

  const mine = (id) => Number(load().r[String(id)]) || 0;
  const canAsk = (id) => {
    const data = load();
    return Boolean(id) && !data.r[String(id)] && data.quiet < Date.now();
  };
  const skip = () => {
    const data = load();
    data.quiet = Date.now() + QUIET_MS;
    save(data);
  };

  // Text/plain: no CORS preflight. Resolves { value, count } (or { saved: true } when the sync lagged).
  const submit = async (id, stars, source) => {
    const data = load();
    const key = keyFor(data, String(id));
    const res = await fetch(`${apiBase()}/api/rating`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ id: String(id), stars, k: key }),
      keepalive: true,
    });
    if (!res.ok) throw new Error(`rating ${res.status}`);
    const result = await res.json().catch(() => ({}));
    data.r[String(id)] = stars;
    save(data);
    if (typeof window.vhpTrack === 'function') window.vhpTrack('recipe_rated', { stars, source: source || '' });
    if (result.count) {
      document.querySelectorAll(`[data-vhp-rating-summary="${id}"]`).forEach((node) => {
        node.hidden = false;
        const text = node.querySelector('[data-vhp-rating-text]');
        if (text) text.textContent = `${fmtValue(result.value)} (${result.count})`;
      });
    }
    document.dispatchEvent(new CustomEvent('vhp:rated', { detail: { id: String(id), stars, ...result } }));
    return result;
  };

  const widget = ({ id, source, onDone } = {}) => {
    const words = tr('rating.words').split(',');
    const el = document.createElement('div');
    el.className = 'vrate';
    el.innerHTML = `<div class="vrate__stars" role="radiogroup" aria-label="${tr('rating.ask')}">${[1, 2, 3, 4, 5]
      .map(
        (n) =>
          `<button type="button" class="vrate__star" role="radio" aria-checked="false" data-stars="${n}" aria-label="${tr('rating.star', { n })}">${STAR}</button>`
      )
      .join('')}</div>
      <p class="vrate__status" aria-live="polite" data-vrate-status></p>
      <button type="button" class="vrate__send" data-vrate-send hidden>${tr('rating.send')}</button>`;
    const buttons = [...el.querySelectorAll('.vrate__star')];
    const status = el.querySelector('[data-vrate-status]');
    const sendBtn = el.querySelector('[data-vrate-send]');
    let chosen = mine(id);
    let busy = false;
    // A tap picks, "Bewertung abgeben" publishes (2026-11-01, docs/PERSONA_ATTACK_PLAN.md 3.5): one curious tap was a
    // public rating.
    let pending = 0;

    const fill = (n) => buttons.forEach((b, i) => b.classList.toggle('is-on', i < n));
    const settle = () => {
      fill(chosen);
      buttons.forEach((b, i) => b.setAttribute('aria-checked', String(i + 1 === chosen)));
    };
    settle();
    status.textContent = chosen ? tr('rating.yours') : '';

    // Preview on hover / focus (mouse and keyboard); touch goes straight to the tap.
    buttons.forEach((b, i) => {
      b.addEventListener('pointerenter', (event) => {
        if (busy || event.pointerType !== 'mouse') return;
        fill(i + 1);
        status.textContent = words[i] || '';
      });
      b.addEventListener('focus', () => !busy && b.matches(':focus-visible') && fill(i + 1));
    });
    el.querySelector('.vrate__stars').addEventListener('pointerleave', () => {
      if (busy) return;
      if (pending) {
        fill(pending);
        status.textContent = words[pending - 1] || '';
        return;
      }
      settle();
      status.textContent = chosen ? tr('rating.yours') : '';
    });

    el.addEventListener('click', async (event) => {
      const b = event.target.closest('[data-stars]');
      if (b && !busy) {
        pending = Number(b.dataset.stars);
        fill(pending);
        buttons.forEach((x, i) => x.setAttribute('aria-checked', String(i + 1 === pending)));
        b.classList.add('is-pop');
        window.setTimeout(() => b.classList.remove('is-pop'), 400);
        status.textContent = words[pending - 1] || '';
        sendBtn.hidden = pending === chosen;
        return;
      }
      if (!event.target.closest('[data-vrate-send]') || busy || !pending) return;
      const stars = pending;
      const before = chosen;
      busy = true;
      chosen = stars;
      pending = 0;
      sendBtn.hidden = true;
      settle();
      el.classList.add('is-busy');
      status.textContent = words[stars - 1] || '';
      try {
        const result = await submit(id, stars, source);
        el.classList.add('is-done');
        status.textContent = result.count ? `${tr('rating.thanks')} ${summary(result)}` : tr('rating.thanks');
        // Then the standing line: another star changes it (docs/UX_REVIEW.md #3).
        window.setTimeout(() => {
          if (!busy && chosen === stars) status.textContent = tr('rating.yours');
        }, 2600);
        try {
          onDone?.(result, stars);
        } catch (_) {}
      } catch (_) {
        chosen = before;
        settle();
        status.textContent = tr('rating.failed');
      } finally {
        busy = false;
        el.classList.remove('is-busy');
      }
    });
    return el;
  };

  // ---------- written review ----------

  const IMAGE_EDGE = 1600;
  const IMAGE_MAX_BYTES = 1400000;
  const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  const canvasBlob = (canvas, quality) => new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

  // Same as the custom recipe photo (assets/vhp-planner-recipes.js): downscale and re-encode as JPEG, which also drops
  // EXIF (location).
  const prepareImage = async (file) => {
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
      await img.decode();
      const ratio = Math.min(1, IMAGE_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.naturalWidth * ratio));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * ratio));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      let quality = 0.82;
      let blob = await canvasBlob(canvas, quality);
      while (blob && blob.size > IMAGE_MAX_BYTES && quality > 0.45) {
        quality -= 0.12;
        blob = await canvasBlob(canvas, quality);
      }
      if (!blob || blob.size > IMAGE_MAX_BYTES) throw new Error('image too large');
      return { blob, image: { type: 'image/jpeg', data: await blobToBase64(blob) } };
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  const REVIEW_ERRORS = {
    name_required: 'review.err_name',
    image_too_large: 'review.err_photo',
    bad_image: 'review.err_photo',
    rate_limited: 'review.err_rate',
    not_paid: 'review.err_not_paid',
    unauthorized: 'review.err_not_paid',
  };

  // Stars inside the form (planner reviews have no one-tap rating before it). Only picks, posts nothing.
  const starPicker = () => {
    const words = tr('rating.words').split(',');
    const el = document.createElement('div');
    el.className = 'vrate vrev-form__stars';
    el.innerHTML = `<div class="vrate__stars" role="radiogroup" aria-label="${esc(tr('review.planner_stars'))}">${[1, 2, 3, 4, 5]
      .map(
        (n) =>
          `<button type="button" class="vrate__star" role="radio" aria-checked="false" data-stars="${n}" aria-label="${esc(tr('rating.star', { n }))}">${STAR}</button>`
      )
      .join('')}</div>
      <p class="vrate__status" aria-live="polite"></p>`;
    const buttons = [...el.querySelectorAll('.vrate__star')];
    const status = el.querySelector('.vrate__status');
    let value = 0;
    el.addEventListener('click', (event) => {
      const b = event.target.closest('[data-stars]');
      if (!b) return;
      value = Number(b.dataset.stars);
      buttons.forEach((x, i) => {
        x.classList.toggle('is-on', i < value);
        x.setAttribute('aria-checked', String(i + 1 === value));
      });
      b.classList.add('is-pop');
      window.setTimeout(() => b.classList.remove('is-pop'), 400);
      status.textContent = words[value - 1] || '';
    });
    return { el, value: () => value };
  };

  // Recipe: after the star tap (stars from mine(id)), one block. target 'planner' (assets/vhp-planner-review.js):
  // one screen per step with a progress bar and "Zurück": stars, then the text, then name + photo (+ consent).
  // Everything after the stars is optional. onLater / onSent let the caller close its sheet; onPrivate(text, stars)
  // (a promise) adds the checkbox "Nur an mich, nicht veröffentlichen" under the text: ticked, "Weiter" becomes
  // "Senden" and the message goes privately to Sören (no review, no name / photo screen).
  const reviewForm = (host, { target = 'recipe', onLater, onSent, onPrivate } = {}) => {
    const planner = target === 'planner';
    const id = host.dataset.vhpRate;
    const acc = account();
    const loggedIn = Boolean(acc.customerId && acc.token);
    const askConsent = loggedIn && host.hasAttribute('data-review-consent');
    const withPhoto = host.dataset.reviewPhoto !== 'false';
    const uid = `vrev-${id}`;
    const picker = planner ? starPicker() : null;
    const opt = `<span class="vrev-form__opt">${esc(tr('review.optional'))}</span>`;
    const el = document.createElement('div');
    el.className = planner ? 'vrev-form vrev-form--steps' : 'vrev-form';
    el.hidden = true;
    el.innerHTML = `<form class="vrev-form__form" novalidate>
        ${
          planner
            ? `<div class="vrev-form__progress" aria-hidden="true" data-vrev-progress><i class="is-on"></i><i></i><i></i></div>
        <div class="vrev-step" data-vrev-step="0" data-vrev-stars></div>`
            : ''
        }
        <div class="vrev-step" data-vrev-step="1"${planner ? ' hidden' : ''}>
          <label class="vrev-form__label" for="${uid}-body">${esc(tr(planner ? 'review.planner_ask' : 'review.ask'))} ${opt}</label>
          <textarea class="vrev-form__input vrev-form__textarea" id="${uid}-body" rows="3" maxlength="2000" placeholder="${esc(tr(planner ? 'review.planner_body_ph' : 'review.body_ph'))}"></textarea>
          ${
            planner && onPrivate
              ? `<label class="vrev-form__check">
            <input type="checkbox" data-vrev-private>
            <span>${esc(tr('review.private'))}</span>
          </label>`
              : ''
          }
        </div>
        <div class="vrev-step" data-vrev-step="2"${planner ? ' hidden' : ''}>
          ${
            planner
              ? `<p class="vrev-form__step-title">${esc(tr('review.you_title'))}</p>
          <p class="vrev-form__why">${esc(tr('review.you_why'))}</p>`
              : ''
          }
          <label class="vrev-form__label" for="${uid}-name">${esc(tr('review.name'))} ${opt}</label>
          <input class="vrev-form__input" id="${uid}-name" maxlength="40" autocomplete="given-name" placeholder="${esc(tr('review.name_ph'))}" value="${esc(host.dataset.name || '')}">
          ${
            withPhoto
              ? `<div class="vrev-form__photo">
            <label class="vrev-form__photo-btn">
              <input type="file" accept="image/*" data-vrev-file>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3v11H4z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><circle cx="12" cy="13" r="3.5" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>
              <span>${esc(tr(planner ? 'review.photo_me' : 'review.photo'))}</span>
            </label>
            <span class="vrev-form__thumb" data-vrev-thumb hidden>
              <img alt="">
              <button type="button" data-vrev-unphoto aria-label="${esc(tr('review.photo_remove'))}">×</button>
            </span>
            <p class="vrev-form__hint">${opt} · ${esc(tr('review.photo_hint'))}</p>
          </div>`
              : ''
          }
          ${
            askConsent
              ? `<label class="vrev-form__check">
            <input type="checkbox" data-vrev-consent>
            <span>${esc(tr('review.consent'))}</span>
          </label>`
              : ''
          }
        </div>
        <input class="vrev-form__hp" name="website" tabindex="-1" autocomplete="off" aria-hidden="true" data-vrev-hp>
        <p class="vrev-form__error" role="alert" data-vrev-error></p>
        <div class="vrev-form__actions">
          ${planner ? `<button type="button" class="vrev-form__send" data-vrev-next hidden>${esc(tr('common.next'))}</button>` : ''}
          <button type="submit" class="vrev-form__send" data-vrev-submit${planner ? ' hidden' : ''}>${esc(tr('review.send'))}</button>
          ${planner ? `<button type="button" class="vrev-form__later" data-vrev-skip hidden>${esc(tr('rating.skip'))}</button>` : ''}
          ${planner ? `<button type="button" class="vrev-form__later" data-vrev-back hidden>${esc(tr('common.back'))}</button>` : ''}
          <button type="button" class="vrev-form__later" data-vrev-later>${esc(tr(planner ? 'common.later' : 'review.later'))}</button>
        </div>
      </form>
      <div class="vrev-form__done" data-vrev-done hidden>
        <p class="vrev-form__done-title">${esc(tr('review.thanks_title'))}</p>
        <p class="vrev-form__done-text" data-vrev-done-text>${esc(tr('review.thanks_text'))}</p>
      </div>`;

    const $ = (sel) => el.querySelector(sel);
    const form = $('form');
    const error = $('[data-vrev-error]');
    const thumb = $('[data-vrev-thumb]');
    const send = $('[data-vrev-submit]');
    let photo = null;
    let previewUrl = '';
    let busy = false;
    if (picker) $('[data-vrev-stars]').appendChild(picker.el);

    // Planner screens: 0 stars (Später), 1 text (Weiter / Überspringen / Zurück), 2 name, photo, consent (Senden /
    // Zurück). One screen at a time; the bar on top shows where they are.
    let step = 0;
    const isPrivate = () => Boolean($('[data-vrev-private]')?.checked);
    const show = (sel, on) => {
      const node = $(sel);
      if (node) node.hidden = !on;
    };
    // The switch: the old screen slides out, the sheet eases to the new height, the new screen slides in (same curve
    // as the sheets, assets/vhp-sheet.js). Reduced motion: an instant swap.
    const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
    const still = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || typeof el.animate !== 'function';
    // Never wait longer than the animation should take (slow / throttled frames would otherwise hold the next tap).
    const done = (anim, ms) => Promise.race([anim.finished.catch(() => {}), new Promise((ok) => window.setTimeout(ok, ms + 60))]);
    // A tap during a switch runs right after it instead of getting lost.
    let moving = false;
    let queued = null;
    const goTo = async (next) => {
      if (!planner) return;
      if (moving) {
        queued = next;
        return;
      }
      if (next === step) return;
      const back = next < step;
      const from = $(`[data-vrev-step="${step}"]`);
      const to = $(`[data-vrev-step="${next}"]`);
      const dx = back ? -1 : 1;
      moving = true;
      if (!still()) {
        const out = from.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: `translateX(${-28 * dx}px)` }], {
          duration: 150,
          easing: 'ease-in',
          fill: 'forwards',
        });
        await done(out, 150);
        out.cancel();
      }
      const h0 = el.offsetHeight;
      step = next;
      from.hidden = true;
      to.hidden = false;
      $('[data-vrev-progress]').querySelectorAll('i').forEach((bar, i) => bar.classList.toggle('is-on', i <= step));
      show('[data-vrev-next]', step === 1);
      show('[data-vrev-skip]', step === 1 && !isPrivate());
      show('[data-vrev-submit]', step === 2);
      show('[data-vrev-back]', step > 0);
      show('[data-vrev-later]', step === 0);
      error.textContent = '';
      if (!still()) {
        const h1 = el.offsetHeight;
        el.style.overflow = 'hidden';
        const grow = el.animate([{ height: `${h0}px` }, { height: `${h1}px` }], { duration: 340, easing: EASE });
        to.animate([{ opacity: 0, transform: `translateX(${36 * dx}px)` }, { opacity: 1, transform: 'none' }], { duration: 340, easing: EASE });
        await done(grow, 340);
        grow.cancel();
        el.style.overflow = '';
      }
      moving = false;
      if (queued !== null) {
        const then = queued;
        queued = null;
        return goTo(then);
      }
      if (step === 1) $('textarea').focus({ preventScroll: true });
    };
    if (picker) {
      picker.el.addEventListener('click', (event) => {
        if (event.target.closest('[data-stars]') && step === 0) window.setTimeout(() => goTo(1), 350);
      });
      $('[data-vrev-back]').addEventListener('click', () => goTo(step - 1));
      const next = $('[data-vrev-next]');
      const nextLabel = () => tr(isPrivate() ? 'review.private_send' : 'common.next');
      $('[data-vrev-private]')?.addEventListener('change', () => {
        next.textContent = nextLabel();
        show('[data-vrev-skip]', !isPrivate());
      });
      next.addEventListener('click', async () => {
        if (!isPrivate()) return goTo(2);
        if (busy) return;
        busy = true;
        next.disabled = true;
        next.textContent = tr('review.sending');
        error.textContent = '';
        try {
          await onPrivate($('textarea').value.trim(), picker.value());
          if (typeof window.vhpTrack === 'function') window.vhpTrack('review_private', { stars: picker.value() });
          $('[data-vrev-done-text]').textContent = tr('review.thanks_private');
          form.hidden = true;
          $('[data-vrev-done]').hidden = false;
          try {
            onSent?.();
          } catch (_) {}
        } catch (_) {
          error.textContent = tr('review.err_failed');
        } finally {
          busy = false;
          next.disabled = false;
          next.textContent = nextLabel();
        }
      });
      $('[data-vrev-skip]').addEventListener('click', () => {
        $('textarea').value = '';
        goTo(2);
      });
    }

    const setPhoto = (prepared) => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = prepared ? URL.createObjectURL(prepared.blob) : '';
      photo = prepared ? prepared.image : null;
      if (!thumb) return;
      thumb.hidden = !prepared;
      thumb.querySelector('img').src = previewUrl;
    };
    $('[data-vrev-file]')?.addEventListener('change', async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      error.textContent = tr('review.photo_prep');
      try {
        setPhoto(await prepareImage(file));
        error.textContent = '';
      } catch (_) {
        error.textContent = tr('review.err_photo');
      }
    });
    $('[data-vrev-unphoto]')?.addEventListener('click', () => setPhoto(null));
    $('[data-vrev-later]').addEventListener('click', () => {
      if (onLater) return onLater();
      el.hidden = true;
      host.querySelector('[data-vrev-open]')?.removeAttribute('hidden');
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (busy) return;
      const body = $('textarea').value.trim();
      const name = $(`#${uid}-name`).value.trim();
      const marketing = Boolean($('[data-vrev-consent]')?.checked);
      const stars = picker ? picker.value() : mine(id);
      const fail = (key) => {
        error.textContent = tr(key);
      };
      error.textContent = '';
      if (!stars) return fail(planner ? 'review.planner_stars' : 'rating.ask');
      // Recipe: the stars are already in, so the form needs something to add. Planner: stars alone are a review.
      if (!planner && !body && !photo) return fail('review.err_empty');
      if (planner && !loggedIn) return fail('review.err_not_paid');

      const data = load();
      const payload = planner
        ? { target: 'planner', stars, body, name }
        : { target: 'recipe', id: String(id), k: keyFor(data, String(id)), stars, body, name };
      if (marketing) payload.marketing = true;
      if (photo) payload.photo = photo;
      const hp = $('[data-vrev-hp]').value;
      if (hp) payload.hp = hp;
      if (loggedIn) Object.assign(payload, { customerId: String(acc.customerId), token: acc.token });

      busy = true;
      send.disabled = true;
      send.textContent = tr('review.sending');
      try {
        const res = await fetch(`${apiBase()}/api/review`, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify(payload),
        });
        const out = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(out.error || String(res.status));
        data.w[String(id)] = Date.now();
        save(data);
        if (typeof window.vhpTrack === 'function') {
          window.vhpTrack('review_sent', { target, stars, photo: photo ? 1 : 0, consent: marketing ? 1 : 0 });
        }
        form.hidden = true;
        $('[data-vrev-done]').hidden = false;
        setPhoto(null);
        try {
          onSent?.();
        } catch (_) {}
      } catch (e) {
        fail(REVIEW_ERRORS[e.message] || 'review.err_failed');
      } finally {
        busy = false;
        send.disabled = false;
        send.textContent = tr('review.send');
      }
    });
    return el;
  };

  const mountReview = (host) => {
    const id = host.dataset.vhpRate;
    const form = reviewForm(host);
    const open = document.createElement('button');
    open.type = 'button';
    open.className = 'vrev-open';
    open.dataset.vrevOpen = '';
    open.textContent = tr('review.write');
    open.hidden = !mine(id);
    const show = () => {
      open.hidden = true;
      form.hidden = false;
    };
    open.addEventListener('click', () => {
      show();
      form.querySelector('textarea')?.focus();
    });
    const note = host.querySelector('.vrp-rate__note');
    host.insertBefore(open, note);
    host.insertBefore(form, note);
    // A fresh tap opens the form right away; changing the stars after a review was sent only offers the link.
    // The sheet replaces its body: a host that left the page stops listening.
    const onRated = (event) => {
      if (!host.isConnected) {
        document.removeEventListener('vhp:rated', onRated);
        return;
      }
      if (String(event.detail?.id) !== String(id)) return;
      if (load().w[String(id)]) open.hidden = !form.hidden;
      else if (form.hidden) show();
    };
    document.addEventListener('vhp:rated', onRated);
  };

  // ---------- review list (snippets/vhp-reviews.liquid) ----------

  let viewer = null;
  const showPhoto = (src, label) => {
    if (!viewer) {
      viewer = document.createElement('dialog');
      viewer.className = 'vrev-viewer';
      viewer.innerHTML = `<img alt=""><button type="button" class="vrev-viewer__close" aria-label="${esc(tr('review.close'))}">×</button>`;
      viewer.addEventListener('click', (event) => {
        if (event.target === viewer || event.target.closest('.vrev-viewer__close')) viewer.close();
      });
      document.body.appendChild(viewer);
    }
    const img = viewer.querySelector('img');
    img.src = src;
    img.alt = label || '';
    viewer.showModal();
  };

  const mountList = (list) => {
    list.querySelector('[data-vrev-more]')?.addEventListener('click', (event) => {
      list.querySelectorAll('[data-vrev-extra]').forEach((item) => {
        item.hidden = false;
      });
      event.currentTarget.remove();
    });
    list.addEventListener('click', (event) => {
      const link = event.target.closest('[data-vrev-photo]');
      if (!link || typeof HTMLDialogElement !== 'function') return;
      event.preventDefault();
      showPhoto(link.href, link.getAttribute('aria-label'));
    });
  };

  // ---------- the rating block (snippets/vhp-reviews.liquid part 'rate', or block() below) ----------

  // Host: [data-vhp-rate=id] with data-source, data-review (written review after the tap), data-review-photo="false",
  // data-name (prefill). onDone(result, stars) after a tap that went through.
  const mountHost = (host, onDone) => {
    if (host.dataset.vrateBound) return;
    host.dataset.vrateBound = '1';
    const slot = host.querySelector('[data-vhp-rate-stars]') || host;
    slot.appendChild(widget({ id: host.dataset.vhpRate, source: host.dataset.source || 'page', onDone }));
    if (host.hasAttribute('data-review')) mountReview(host);
  };

  // The same block built in JS (cook mode): { id, source, review, photo, note, title, text, heading, name, onDone }.
  // Defaults match the snippet; title / text false leave them out (the caller has its own).
  const block = ({ id, source = '', review = true, photo = true, note = true, title, text, heading = 'h3', name = '', onDone } = {}) => {
    const host = document.createElement('section');
    host.className = 'vrp-rate';
    host.dataset.vhpRate = String(id);
    host.dataset.source = source;
    if (review) host.setAttribute('data-review', '');
    if (!photo) host.dataset.reviewPhoto = 'false';
    if (name) host.dataset.name = name;
    const add = (tag, className, value) => {
      const node = document.createElement(tag);
      node.className = className;
      node.textContent = value;
      host.appendChild(node);
    };
    if (title !== false) add(heading, 'vrp-rate__title', title || tr('rating.block_title'));
    if (text !== false) add('p', 'vrp-rate__text', text || tr('rating.block_text'));
    const stars = document.createElement('div');
    stars.setAttribute('data-vhp-rate-stars', '');
    host.appendChild(stars);
    if (note) add('p', 'vrp-rate__note', tr('rating.note'));
    mountHost(host, onDone);
    return host;
  };

  // Everything in root (default: the page) not mounted yet. The recipe sheet calls it for each body it shows.
  const mountAll = (root = document) => {
    root.querySelectorAll('[data-vhp-rate]:not([data-vrate-bound])').forEach((host) => mountHost(host));
    root.querySelectorAll('[data-vrev]:not([data-vrev-bound])').forEach((list) => {
      list.dataset.vrevBound = '1';
      mountList(list);
    });
  };

  window.vhpRating = { widget, block, mine, canAsk, skip, submit, reviewForm, mount: mountAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountAll());
  else mountAll();
})();
